import React from 'react';
import './InsuranceBanner.css';
import './Loader.css';
import MyPageButton from '../MyPageButton';
import AppBrand from '../AppBrand';
import Elephant from '../AppBrand/Elephant';
import { inject, observer } from 'mobx-react';
import { format } from 'date-fns';
import { api } from '../../../services';
import K from '../../../constants';
import moment from 'moment';
import { message } from 'antd';
import { useInsuranceData } from './useInsuranceData';
import { ExpiringCountdown } from './ExpiringCountdown';

const insuranceNameImgMapper = {
  air: 'air.webp',
  allianz: 'allianz.webp',
  argo: 'argo.webp',
  assurant: 'assurant.webp',
  axa: 'axa.webp',
  conte: 'conte.webp',
  default: 'default.webp',
  fit2you: 'fit2you.webp',
  ima: 'ima.webp',
  nobis: 'nobis.webp',
  prima: 'prima.webp',
  quixa: 'quixa.webp',
  revo: 'revo.webp',
  tua: 'tua.webp',
  uca: 'uca.webp',
  verti: 'verti.webp',
  vittoria: 'vittoria.webp',
  zurich: 'zurich.webp',
};

const InsuranceBanner = (props) => {
  const {
    mypageStore,
    authStore,
    translationsStore,
    size,
    insurance: propInsurance,
    price: propPrice,
    waitingForPrice: propWaitingForPrice,
  } = props;

  const { state, handleLoadingToken } = useInsuranceData(
    mypageStore,
    authStore,
    propInsurance,
    propPrice,
    propWaitingForPrice
  );

  // Get insurance from props or from store
  const insurance = propInsurance || mypageStore.selectedVehicleRCAInsurance;

  const isInsuranceExpired =
    insurance && new Date(insurance.node.date) < new Date();

  // insurance not issued by fit2you and active (expiring date > 60 days)
  const isInsuranceNotFit2YouActive =
    insurance &&
    insurance.node.channel !== 'fit2you' &&
    moment(insurance.node.date).isAfter(moment().add(60, 'days'));

  // insurance issued by fit2you and Expiring (expiring date <= 60 days)
  const isInsuranceFit2YouExpiring =
    insurance &&
    insurance.node.channel === 'fit2you' &&
    moment(insurance.node.date).isBetween(moment(), moment().add(60, 'days'));

  // Calculate final price with potential discount
  let definitivePrice = state.price;
  if (state.price && state.discountExpirationDate && state.discountPrice) {
    if (moment().isBefore(moment(state.discountExpirationDate))) {
      definitivePrice = state.discountPrice;
    }
  }

  let topSectionRCAuto;
  let bottomSectionRCAuto;
  let showTrustpilot = false;
  let showCountdown = false;
  let title = translationsStore.translate('InsuranceBanner.title.offer');

  // Button component with token fetching logic
  let button = (
    <MyPageButton
      theme='salmon'
      className='InsuranceBannerButton'
      solid
      title={
        isInsuranceFit2YouExpiring
          ? translationsStore.translate('InsuranceBanner.button.renew')
          : translationsStore.translate('InsuranceBanner.button.makeMeSave')
      }
      style={{ width: 184, height: 39 }}
      onClick={async () => {
        const userUuid = authStore.user
          ? authStore.user.attributes['custom:external_uuid']
          : undefined;

        try {
          handleLoadingToken(true);
          const data = await api(
            'getAgendaInsuranceToken',
            userUuid,
            mypageStore.selectedManageableVehicle.uuid
          );
          if (data.token === undefined) {
            throw new Error('token is undefined');
          }
          window.location.href = `${K.FIT2YOU_INSURANCE_OFFER_URL}/guest-login?token=${data.token}`;
        } catch (error) {
          message.error(
            translationsStore.translate('InsuranceBanner.error.message')
          );
          console.error(error);
        } finally {
          handleLoadingToken(false);
        }
      }}
    />
  );

  // Set content based on insurance status
  if (!insurance || isInsuranceNotFit2YouActive) {
    title = translationsStore.translate('InsuranceBanner.title.news');
    topSectionRCAuto = (
      <span>
        {translationsStore.translate('InsuranceBanner.sectionComeBack.top1')}
        <span style={{ fontWeight: 700 }}>
          {translationsStore.translate('InsuranceBanner.sectionComeBack.top2')}
        </span>
      </span>
    );

    bottomSectionRCAuto = (
      <span style={{ lineHeight: 1 }}>
        {translationsStore.translate('InsuranceBanner.sectionComeBack.bottom')}
      </span>
    );
    button = <></>;
  } else if (isInsuranceFit2YouExpiring) {
    topSectionRCAuto = (
      <span style={{ fontWeight: 700 }}>
        {translationsStore.translate('InsuranceBanner.sectionExpiring.top')}
      </span>
    );

    bottomSectionRCAuto = (
      <span>
        <span className='Label'>
          {translationsStore.translate(
            'InsuranceBanner.sectionExpiring.bottom1'
          )}
        </span>
        &nbsp;
        {translationsStore.translate('InsuranceBanner.sectionExpiring.bottom2')}
        {format(insurance.node.date, 'DD/MM/YYYY')}
      </span>
    );
  } else if (state.waitingForPrice) {
    showTrustpilot = true;
    showCountdown = true;
    topSectionRCAuto = (
      <span className='TopSection'>
        {translationsStore.translate(
          isInsuranceExpired
            ? 'InsuranceBanner.title.expired'
            : 'InsuranceBanner.title.expiring'
        )}
      </span>
    );

    bottomSectionRCAuto = (
      <span
        dangerouslySetInnerHTML={{
          __html: translationsStore.translate(
            'InsuranceBanner.prezzoCivettaText'
          ),
        }}
      />
    );
  } else if (definitivePrice) {
    showTrustpilot = true;
    showCountdown = true;
    topSectionRCAuto = (
      <span className='TopSection'>
        {translationsStore.translate(
          isInsuranceExpired
            ? 'InsuranceBanner.title.expired'
            : 'InsuranceBanner.title.expiring'
        )}
      </span>
    );

    bottomSectionRCAuto = (
      <span
        dangerouslySetInnerHTML={{
          __html: translationsStore
            .translate('InsuranceBanner.sectionExactPrice.bottom1')
            .replace('{price}', definitivePrice),
        }}
      />
    );
  } else {
    showTrustpilot = true;
    showCountdown = true;
    topSectionRCAuto = (
      <span style={{ fontWeight: 700 }}>
        {translationsStore.translate(
          isInsuranceExpired
            ? 'InsuranceBanner.title.expired'
            : 'InsuranceBanner.title.expiring'
        )}
      </span>
    );

    bottomSectionRCAuto = (
      <span
        dangerouslySetInnerHTML={{
          __html: translationsStore.translate(
            'InsuranceBanner.prezzoCivettaText'
          ),
        }}
      />
    );
  }
  const [isAccordionOpen, setIsAccordionOpen] = React.useState(true);

  const toggleAccordion = () => {
    setIsAccordionOpen(!isAccordionOpen);
  };
  if (size === 'mobile' && showTrustpilot) {
    return (
      <div className='InsuranceBannerMobile'>
        <div className='InsuranceBannerHeader'>
          <div className='InsuranceBannerLogo'>
            <AppBrand
              style={{
                width: 33,
                height: 19,
                color: 'var(--action-brand-color)',
              }}
            />
            <Elephant style={{ width: 22, height: 28 }} />
          </div>

          {definitivePrice && (
            <div className='InsuranceBannerDiscount'>
              {translationsStore.translate('InsuranceBanner.15PercentDiscount')}
            </div>
          )}
        </div>

        {definitivePrice && (
          <div className='InsuranceBannerCompanySection'>
            <img
              alt={`${state.companyName ? state.companyName : 'default'} logo`}
              src={`insurance/${insuranceNameImgMapper[
                state.companyName || ''
              ] || insuranceNameImgMapper.default}`}
              className='InsuranceBannerCompanyLogo'
            />

            <div className='InsuranceBannerOfferText'>
              <span>
                {translationsStore.translate('InsuranceBanner.offerRCA')}
              </span>
              <span className='InsuranceBannerPrice'>{definitivePrice}€</span>
            </div>
          </div>
        )}

        <div className='InsuranceBannerExpirySection'>
          <p
            className={`InsuranceBannerTitle ${
              !definitivePrice ? 'big-text' : ''
            }`}
          >
            {translationsStore.translate(
              isInsuranceExpired
                ? 'InsuranceBanner.title.expired'
                : 'InsuranceBanner.title.expiring'
            )}
          </p>

          {showCountdown && insurance && (
            <div className='InsuranceBannerCountdown'>
              <ExpiringCountdown targetDate={new Date(insurance.node.date)} />
            </div>
          )}

          {!definitivePrice && (
            <>
              <div className='InsuranceBannerDiscount'>
                {translationsStore.translate(
                  'InsuranceBanner.15PercentDiscount'
                )}
              </div>
              <div>
                {translationsStore.translate(
                  'InsuranceBanner.calcYourEstimateNow'
                )}
              </div>
            </>
          )}

          <div className='InsuranceBannerStars'>
            <span className='stars'>★★★★★ </span>
            <span>
              {translationsStore.translate('InsuranceBanner.onTrustpilot')}
            </span>
          </div>
        </div>

        <div className='InsuranceBannerButtonContainer'>
          {state.loadingToken ? <div className='loader' /> : button}
        </div>

        <div
          className='InsuranceBannerMoreDetails'
          onClick={toggleAccordion}
          role='button'
          aria-expanded={isAccordionOpen}
          tabIndex={0}
        >
          {translationsStore.translate('InsuranceBanner.moreDetails')}{' '}
          <span
            className='accordion-icon'
            style={{
              transform: isAccordionOpen ? 'rotate(270deg)' : 'rotate(90deg)',
              display: 'inline-block',
              transition: 'transform 0.3s ease',
            }}
          >
            {'>'}
          </span>
        </div>

        <div
          className='InsuranceBannerTrustpilotInfo'
          style={{
            maxHeight: isAccordionOpen ? '500px' : '0px',
            overflow: 'hidden',
            opacity: isAccordionOpen ? 1 : 0,
            visibility: isAccordionOpen ? 'visible' : 'hidden',
            transition:
              'max-height 0.3s ease-in-out, opacity 0.5s ease-in-out, visibility 0.5s',
          }}
        >
          <TrustpilotSection
            mode={definitivePrice ? 'price' : 'civetta'}
            size={'mobile'}
          />
        </div>
      </div>
    );
  }

  // Render mobile layout
  if (size === 'mobile') {
    return (
      <div style={{ minHeight: 207 }} className={'InsuranceBannerContainer'}>
        <div className={'InsuranceBannerMainSectionMobile'}>
          <div className={'InsuranceBannerLogo'}>
            <AppBrand
              style={{
                width: 33,
                height: 19,
                color: 'var(--action-brand-color)',
              }}
            />
            <Elephant style={{ width: 22, height: 28 }} />
          </div>
          <div className={'InsuranceBannerTitle'} style={{ width: 126 }}>
            {title}
          </div>
          <img
            alt={`${state.companyName ? state.companyName : 'default'} logo`}
            src={`insurance/${insuranceNameImgMapper[state.companyName || ''] ||
              insuranceNameImgMapper.default}`}
            style={{ marginTop: 18, marginLeft: 22 }}
            className={'InsuranceBannerImg'}
          />
        </div>

        <div style={{ marginLeft: 16, marginBottom: 11 }}>
          {topSectionRCAuto}
          <br />
          {bottomSectionRCAuto}
        </div>

        {state.loadingToken ? <div className='loader' /> : button}
      </div>
    );
  }

  // Render desktop layout
  return (
    <div className={showTrustpilot ? 'InsuranceBannerDesktopContainer' : ''}>
      <div className={'InsuranceBannerContainer'}>
        {!showTrustpilot && (
          <div className={'InsuranceBannerTitle'}>{title}</div>
        )}

        <div className={'InsuranceBannerMainSection'}>
          <div className={'InsuranceBannerInnerSection'}>
            <img
              alt={`${state.companyName ? state.companyName : 'default'} logo`}
              src={`insurance/${
                insuranceNameImgMapper[state.companyName]
                  ? insuranceNameImgMapper[state.companyName]
                  : insuranceNameImgMapper.default
              }`}
              className={'InsuranceBannerImg'}
            />
          </div>

          <div
            style={{
              marginTop:
                isInsuranceNotFit2YouActive || isInsuranceFit2YouExpiring
                  ? 0
                  : -14,
            }}
            className={'InsuranceBannerInnerSection RCAutoSection'}
          >
            {topSectionRCAuto}
            {showCountdown && (
              <ExpiringCountdown targetDate={new Date(insurance.node.date)} />
            )}
            <div>{bottomSectionRCAuto}</div>
          </div>
          <div className={'InsuranceBannerInnerSection'}>
            {state.loadingToken ? <div className='loader' /> : button}
          </div>
          <div className={'InsuranceBannerLogo'}>
            <AppBrand
              style={{
                width: 33,
                height: 19,
                color: 'var(--action-brand-color)',
              }}
            />
            <Elephant style={{ width: 22, height: 28 }} />
          </div>
        </div>
      </div>
      {showTrustpilot && (
        <TrustpilotSection mode={definitivePrice ? 'price' : 'civetta'} />
      )}
    </div>
  );
};
export default inject(
  'mypageStore',
  'authStore',
  'translationsStore'
)(observer(InsuranceBanner));

const TrustpilotSectionTest = (props) => {
  const { translationsStore, mode, size } = props;

  if (size === 'mobile') {
    return (
      <div
        className='trustpilot-section'
        dangerouslySetInnerHTML={{
          __html: translationsStore.translate(
            mode === 'price'
              ? 'InsuranceBanner.trustpilotMobile'
              : 'InsuranceBanner.trustpilotCivettaMobile'
          ),
        }}
      ></div>
    );
  }

  return (
    <div
      className='trustpilot-section'
      dangerouslySetInnerHTML={{
        __html: translationsStore.translate(
          mode === 'price'
            ? 'InsuranceBanner.trustpilot'
            : 'InsuranceBanner.trustpilotCivetta'
        ),
      }}
    />
  );
};

const TrustpilotSection = inject('translationsStore')(
  observer(TrustpilotSectionTest)
);
